import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import authService from '../services/authService';

/**
 * Authentication Context
 * Provides authentication state and methods to the entire application
 */
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize authentication state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true);
        
        // Try to get user from localStorage first for immediate UI rendering
        const storedUser = authService.getCurrentUser();
        if (storedUser) {
          setUser(storedUser);
        }
        
        // Then validate the token with the server in the background
        const token = localStorage.getItem('token');
        if (token) {
          try {
            const userData = await authService.validateToken();
            setUser(userData);
          } catch (validationError) {
            // Token validation failed, clear user state
            setUser(null);
          }
        }
      } catch (err) {
        console.error('Authentication initialization error:', err);
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []);

  /**
   * Login user
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} - Logged in user
   */
  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const { user: userData } = await authService.login(email, password);
      setUser(userData);
      
      return userData;
    } catch (err) {
      setError(err.message || 'Failed to login. Please check your credentials.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} - Registered user
   */
  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);
      
      const { user: newUser } = await authService.register(userData);
      setUser(newUser);
      
      return newUser;
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout user
   * @returns {Promise<void>}
   */
  const logout = useCallback(async () => {
    try {
      setLoading(true);
      await authService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setLoading(false);
    }
  }, []);

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise<void>}
   */
  const forgotPassword = async (email) => {
    try {
      setLoading(true);
      setError(null);
      
      return await authService.forgotPassword(email);
    } catch (err) {
      setError(err.message || 'Failed to process password reset. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reset password
   * @param {string} token - Reset token
   * @param {string} newPassword - New password
   * @returns {Promise<void>}
   */
  const resetPassword = async (token, newPassword) => {
    try {
      setLoading(true);
      setError(null);
      
      return await authService.resetPassword(token, newPassword);
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update user profile
   * @param {Object} profileData - Profile data to update
   * @returns {Promise<Object>} - Updated user profile
   */
  const updateProfile = async (profileData) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('AuthContext.updateProfile - Calling authService with:', profileData);
      const updatedUser = await authService.updateProfile(profileData);
      console.log('AuthContext.updateProfile - Received from authService:', updatedUser);
      
      // Update the user state with the new data
      setUser(updatedUser);
      console.log('AuthContext.updateProfile - User state updated');
      
      return updatedUser;
    } catch (err) {
      setError(err.message || 'Failed to update profile. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Upload profile image
   * @param {File} imageFile - Image file to upload
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<Object>} - Updated user profile
   */
  const uploadProfileImage = async (imageFile, onProgress) => {
    try {
      setLoading(true);
      setError(null);
      
      const updatedUser = await authService.uploadProfileImage(imageFile, onProgress);
      
      // Update the user state with the new image
      const newUser = {
        ...user,
        profileImage: updatedUser.profileImage
      };
      setUser(newUser);
      
      // Also update localStorage to ensure persistence
      localStorage.setItem('user', JSON.stringify(newUser));
      
      return updatedUser;
    } catch (err) {
      setError(err.message || 'Failed to upload profile image. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Delete profile image
   * @returns {Promise<Object>} - Updated user profile without image
   */
  const deleteProfileImage = async () => {
    try {
      setLoading(true);
      setError(null);

      const updatedUser = await authService.deleteProfileImage();

      // Update the user state to remove the image
      const newUser = {
        ...user,
        profileImage: null
      };
      setUser(newUser);

      // Also update localStorage to ensure persistence
      localStorage.setItem('user', JSON.stringify(newUser));

      return updatedUser;
    } catch (err) {
      setError(err.message || 'Failed to delete profile image. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Change user password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} - Change password result
   */
  const changePassword = async (currentPassword, newPassword) => {
    try {
      setLoading(true);
      setError(null);
      
      return await authService.changePassword(currentPassword, newPassword);
    } catch (err) {
      setError(err.message || 'Failed to change password. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if user has specific permission (path-based with wildcard support)
   * @param {string} permissionPath - Permission path (e.g. 'project.foundation.analysis.edit')
   * @param {string} projectId - Optional project ID for project-level permissions
   * @returns {boolean}
   */
  const hasPermission = useCallback((permissionPath, projectId = null, { exact = false } = {}) => {
    if (!user) return false;

    // Super admin bypass
    if (user.isSuperAdmin) return true;

    // Gather effective permissions
    let effectivePermissions = user.permissions || [];

    // Project-level permissions disabled — using system-level only
    // To re-enable, uncomment below:
    // if (projectId && user.projectLevelRole && user.projectLevelRole[projectId]) {
    //   const projectPerms = user.projectLevelRole[projectId].permissions || [];
    //   effectivePermissions = [...effectivePermissions, ...projectPerms];
    // }

    // Check for match (direct, wildcard, or child-of)
    for (const perm of effectivePermissions) {
      if (perm === '*') return true;
      if (perm === permissionPath) return true;
      // Wildcard: 'project.*' grants 'project.foundation.edit'
      if (perm.endsWith('.*')) {
        const prefix = perm.slice(0, -2);
        if (permissionPath.startsWith(prefix + '.') || permissionPath === prefix) {
          return true;
        }
      }
      // Child-of: having 'header.createproject.create' grants 'header.createproject'
      // Skip this rule in exact mode
      if (!exact && perm.startsWith(permissionPath + '.')) {
        return true;
      }
    }

    return false;
  }, [user]);

  /**
   * Refresh user data from the server
   * @returns {Promise<Object>} - Fresh user data
   */
  const refreshUserData = async () => {
    try {
      setLoading(true);

      const freshUserData = await authService.validateToken();
      setUser(freshUserData);

      return freshUserData;
    } catch (err) {
      console.error('Error refreshing user data:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Silent permission refresh (no loading spinner)
  const isRefreshing = useRef(false);
  const userRef = useRef(user);
  userRef.current = user;

  const silentRefreshPermissions = useCallback(async () => {
    if (!userRef.current || isRefreshing.current) return;
    isRefreshing.current = true;
    try {
      const freshUserData = await authService.validateToken();
      const oldData = JSON.stringify({
        perms: userRef.current.permissions || [],
        superAdmin: userRef.current.isSuperAdmin,
        sysRole: userRef.current.systemLevelRole,
        projRoles: userRef.current.projectLevelRole
      });
      const newData = JSON.stringify({
        perms: freshUserData.permissions || [],
        superAdmin: freshUserData.isSuperAdmin,
        sysRole: freshUserData.systemLevelRole,
        projRoles: freshUserData.projectLevelRole
      });
      if (oldData !== newData) {
        setUser(freshUserData);
      }
    } catch (err) {
      // Silently ignore — don't disrupt the user
    } finally {
      isRefreshing.current = false;
    }
  }, []); // No dependencies — uses refs

  // Auto-refresh permissions every 30 seconds, on window focus, and on route change
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(silentRefreshPermissions, 30 * 1000);

    const handleFocus = () => silentRefreshPermissions();
    window.addEventListener('focus', handleFocus);

    // Refresh on route navigation
    const handleRouteChange = () => silentRefreshPermissions();
    window.addEventListener('popstate', handleRouteChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [!!user, silentRefreshPermissions]); // Only re-run when user becomes truthy/falsy

  const value = {
    user,
    loading,
    error,
    isInitialized,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    updateProfile,
    uploadProfileImage,
    deleteProfileImage,
    changePassword,
    hasPermission,
    refreshUserData,
    refreshPermissions: silentRefreshPermissions,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};